import { bind, classNames, Component, h, tag } from "omi";
import confetti from "canvas-confetti";
import isEmpty from "lodash-es/isEmpty";

import { IconName, NWIcon, NWLoadingSpinner } from "@/components";
import { tailwind } from "@/tailwind";

export type Props = {
  withConfetti?: boolean;
  icon?: IconName;
  size: "fit" | "md" | "lg" | "xl" | "2xl" | "full" | "icon";
  iconSize?: "sm" | "md" | "lg";
  iconPadding?: "none" | "sm";
  iconColor?: string;
  iconBorder?: boolean;
  backgroundTransparent?: boolean;
  theme?: "light" | "dark";
  type?: "primary" | "secondary" | "secondary-color" | "text" | "icon" | "danger";
  disabled?: boolean;
  loading?: boolean;
  loadingColor?: "brand" | "white";
  loadingText?: string;
  href?: string;
  subtext?: string;
  subtextColor?: string;
};

@tag("nw-button")
export class NWButton extends Component<Props> {
  static css = [tailwind];

  static propTypes = {
    withConfetti: Boolean,
    icon: String,
    size: String,
    type: String,
    disabled: Boolean,
    loading: Boolean,
    loadingText: String,
    loadingColor: String,
    href: String,
    iconColor: String,
    iconSize: String,
    iconPadding: String,
    iconBorder: Boolean,
    backgroundTransparent: Boolean,
    theme: String,
    subtext: String,
    subtextColor: String,
  };

  @bind
  private handleOnClick() {
    if (this.rootElement == null) {
      return;
    }

    if (this.props.withConfetti) {
      const btnPosition = this.rootElement?.getBoundingClientRect();
      const originY = (btnPosition.top + btnPosition.bottom) / 2 / window.innerHeight;
      const originX = (btnPosition.left + btnPosition.right) / 2 / window.innerWidth;

      confetti({
        particleCount: 300,
        origin: {
          y: originY,
          x: originX,
        },
        spread: 60,
      });
    }
  }

  private getIconDimensions(size: Props["size"], iconSize: Props["iconSize"]): { width: number; height: number } {
    if (iconSize) {
      switch (iconSize) {
        case "sm":
          return { width: 16, height: 16 };
        case "md":
          return { width: 20, height: 20 };
        case "lg":
          return { width: 24, height: 24 };
      }
    }

    // If iconSize is not specified, fall back to the previous logic
    switch (size) {
      case "xl":
        return { width: 28, height: 28 };

      case "full":
      case "lg":
      case "md":
      default:
        return { width: 20, height: 20 };
    }
  }

  render({
    loadingText,
    disabled = false,
    type = "primary",
    loading = false,
    iconSize,
    iconColor,
    theme = "light",
  }: Props) {
    const { width, height } = this.getIconDimensions(this.props.size, iconSize);
    const isDisabled = disabled || loading;
    const Tag = { name: this.props.href ? "a" : "button" };
    return (
      <>
        <Tag.name
          href={this.props.href ? this.props.href : undefined}
          target={this.props.href ? "_blank" : undefined}
          type={this.props.href ? undefined : "submit"}
          disabled={isDisabled}
          class={classNames(
            "flex items-center justify-center transition-all duration-300 rounded-lg font-semibold outline-none",
            {
              "bg-leather": theme === "dark",
              "bg-white": theme === "light",
            },
            {
              "border border-leather !bg-leather hover:!bg-brand-900 text-white focus-visible:shadow-ring-sxs":
                type === "primary" && !disabled,
              "bg-gray-100 shadow-xs border border-gray-200 text-tertiary cursor-not-allowed":
                type === "primary" && disabled,
            },
            {
              "border border-error-400 !bg-error-400 hover:!bg-error-500 text-white focus-visible:shadow-ring-error-sxs":
                type === "danger" && !disabled,
              "bg-gray-100 shadow-xs border border-gray-200 text-tertiary cursor-not-allowed":
                type === "danger" && disabled,
            },
            {
              "border border-gray-300 bg-gray-25 hover:bg-gray-50 text-primary focus-visible:shadow-ring-sxs":
                type === "secondary",
            },
            {
              "border !bg-primary border-brand-200 hover:!bg-primary-hover hover:text-brand-900 text-leather focus-visible:shadow-ring-sxs":
                type === "secondary-color" && !disabled,
              "shadow-xs border border-gray-200 text-disabled": type === "secondary-color" && disabled,
            },
            {
              "!bg-transparent hover:text-primary text-leather hover:!bg-gray-50 focus-visible:shadow-sxs":
                type === "text" && !disabled,
              "text-disabled": type === "text" && disabled,
            },
            {
              // TODO should this be text-tertiary + the tertiary-hover?
              "hover:text-gray-700 text-gray-600 focus-visible:shadow-ring-sxs": type === "icon" && !disabled,
              "text-disabled": type === "icon" && disabled,
              "bg-brand-900 hover:bg-brand-800": type === "icon" && theme === "dark" && !disabled,
              "hover:bg-gray-50": type === "icon" && theme === "light" && !disabled,
            },
            // {
            //   "hover:text-gray-700 text-gray-600 hover:bg-gray-50 focus-visible:shadow-ring-sxs":
            //     (type === "text" || type === "icon") && !disabled,
            //   "text-gray-600": (type === "text" || type === "icon") && disabled,
            // },
            {
              "border border-gray-200": this.props.iconBorder,
            },
            {
              "w-full py-2.5 px-4": this.props.size === "full",
              "w-full sm:w-[460px] py-4 text-lg": this.props.size === "xl",
              "w-full self-stretch py-4 px-5 text-lg": this.props.size === "2xl",
              "px-4 py-2.5": this.props.size === "lg",
              "w-40 py-3 px-4 text-sm": this.props.size === "md",
              "w-fit py-2 px-3 text-sm": this.props.size === "fit",
              [`w-fit text-sm ${this.props.iconPadding === "none" ? "" : "p-2"}`]: this.props.size === "icon",
            },
            {
              "!mb-1.5": this.props.subtext,
            },
            {
              "cursor-not-allowed": isDisabled,
              "cursor-pointer": !isDisabled,
            },
            {
              "!bg-transparent": this.props.backgroundTransparent,
            },
          )}
          onClick={disabled ? undefined : this.handleOnClick}
        >
          {loading && (
            <NWLoadingSpinner
              size="sm"
              padding="none"
              color={this.props.loadingColor ?? (type === "primary" ? "white" : "brand")}
            ></NWLoadingSpinner>
          )}
          {!loading && this.props.icon && (
            <NWIcon
              name={this.props.icon}
              slot="prefix"
              width={width}
              height={height}
              class={classNames(
                {
                  "mr-2": this.props.size !== "xl" && type !== "icon",
                  "mr-3": this.props.size === "xl",
                },
                iconColor,
              )}
            ></NWIcon>
          )}
          <slot class={classNames("select-none", { hidden: loading })}></slot>
          {loading && loadingText && <span class="ml-2 select-none">{this.props.loadingText}</span>}
        </Tag.name>
        {!isEmpty(this.props.subtext) && (
          <p class={classNames("text-xs text-center w-full", this.props.subtextColor ?? "text-tertiary")}>
            {this.props.subtext}
          </p>
        )}
      </>
    );
  }
}
